import React, { useState, useEffect } from "react";
import {
  FiChevronDown,
  FiMenu,
  FiX,
  FiPhone,
  FiHome,
  FiUsers,
  FiCheck,
  FiAlertCircle,
} from "react-icons/fi";
import {
  MdConstruction,
  MdDesignServices,
  MdApartment,
  MdHomeWork,
} from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import logo from "../Assert/LogoDesign/Logo.webp";

const ConstructionNavbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectsData, setProjectsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const location = useLocation();
  const isDesktop = windowWidth >= 1025;

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 1025) setSidebarOpen(false);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Fetch projects data
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "https://dev.mithahomes.com/backend/api/mainprojectprocess/nav",
        );

        if (response.data.success) {
          setProjectsData(response.data.data);
        } else {
          setError("Failed to fetch projects");
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Failed to load projects. Please try again later.");
        // Fallback data
        setProjectsData({
          "Ready to Occupy": [
            { pid: 64, project_name: "Mitha Amethyst" },
            { pid: 65, project_name: "Mitha Amethyst" },
          ],
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const toggleMobileDropdown = (dropdown) => {
    setMobileDropdown(mobileDropdown === dropdown ? null : dropdown);
  };

  const handleMobileLinkClick = () => {
    setSidebarOpen(false);
    setMobileDropdown(null);
  };

  const isActiveRoute = (routePath) => {
    return (
      location.pathname === routePath ||
      location.pathname.startsWith(routePath + "/")
    );
  };

  // Navigation items
  const navItems = [
    { id: "home", label: "Home", path: "/" },
    { id: "about", label: "About Us", path: "/about", dropdown: true },
    { id: "projects", label: "Projects", path: "", dropdown: true },
    { id: "testimonials", label: "Testimonials", path: "/testimonials" },
    { id: "blog", label: "Blog", path: "/blog" },
    { id: "contact", label: "Contact Us", path: "/contact" },
  ];

  const aboutDropdownItems = [
    { text: "History", icon: <FiUsers size={16} />, path: "/about" },
    {
      text: "Vision Mission",
      icon: <FiCheck size={16} />,
      path: "/about/vision-mission",
    },
  ];

  // Get projects by category (Ongoing first, Completed second, lowest PID first)
  const getProjectsByCategory = () => {
    const grouped = {};

    // First, get categories from the API response
    const apiCategories = Object.keys(projectsData);

    // Sort categories: Ongoing projects first, then Completed projects
    const sortedCategories = [...apiCategories].sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();

      // Check if category contains "ongoing", "current", "under construction", etc.
      const aIsOngoing =
        aLower.includes("ongoing") ||
        aLower.includes("current") ||
        aLower.includes("under construction");

      const bIsOngoing =
        bLower.includes("ongoing") ||
        bLower.includes("current") ||
        bLower.includes("under construction");

      // Check if category contains "completed", "ready", "occupy"
      const aIsCompleted =
        aLower.includes("completed") ||
        aLower.includes("ready") ||
        aLower.includes("occupy");

      const bIsCompleted =
        bLower.includes("completed") ||
        bLower.includes("ready") ||
        bLower.includes("occupy");

      // Ongoing projects FIRST
      if (aIsOngoing && !bIsOngoing) return -1;
      if (!aIsOngoing && bIsOngoing) return 1;

      // Completed projects SECOND
      if (aIsCompleted && !bIsCompleted) return 1;
      if (!aIsCompleted && bIsCompleted) return -1;

      // For other categories, maintain original order
      return 0;
    });

    // Now process each category in the sorted order
    sortedCategories.forEach((category) => {
      const projects = projectsData[category];
      if (!projects || !Array.isArray(projects)) return;

      // Sort projects by PID in ASCENDING order (lowest PID first)
      const sortedProjects = [...projects].sort((a, b) => {
        // Convert to numbers for proper numeric sorting
        const pidA = Number(a.pid);
        const pidB = Number(b.pid);
        return pidA - pidB; // Ascending order (lowest PID first)
      });

      if (!grouped[category]) {
        grouped[category] = [];
      }

      // Choose appropriate icon based on category
      let icon;
      const categoryLower = category.toLowerCase();

      if (
        categoryLower.includes("ongoing") ||
        categoryLower.includes("current") ||
        categoryLower.includes("under construction")
      ) {
        icon = <MdConstruction size={16} />; // Construction icon for ongoing
      } else if (
        categoryLower.includes("completed") ||
        categoryLower.includes("ready") ||
        categoryLower.includes("occupy")
      ) {
        icon = <MdHomeWork size={16} />; // Home icon for completed
      } else {
        icon = <MdApartment size={16} />; // Apartment icon for others
      }

      sortedProjects.forEach((project) => {
        grouped[category].push({
          text: project.project_name,
          icon: icon,
          path: `/project/${project.pid}`,
        });
      });
    });

    return grouped;
  };

  const projectsByCategory = getProjectsByCategory();

  // Styles
  const styles = {
    appWrapper: {
      fontFamily: "'Inter', system-ui, sans-serif",
    },

    // Desktop Header
    header: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "80px",
      display: isDesktop ? "flex" : "none",
      alignItems: "center",
      background: isScrolled ? "rgba(255,255,255,0.98)" : "#ffffff",
      boxShadow: isScrolled ? "0 8px 32px rgba(15,23,42,0.1)" : "none",
      padding: "0 24px",
      zIndex: 1000,
      backdropFilter: isScrolled ? "blur(20px)" : "none",
      transition: "all 0.3s ease",
      borderBottom: isScrolled ? "1px solid rgba(226, 232, 240, 0.8)" : "none",
    },

    container: {
      maxWidth: "1280px",
      margin: "0 auto",
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },

    logo: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      cursor: "pointer",
      textDecoration: "none",
      color: "inherit",
    },

    logoImage: {
      height: isScrolled ? "45px" : "55px",
      width: "auto",
      objectFit: "contain",
      transition: "all 0.3s ease",
    },

    nav: {
      display: isDesktop ? "flex" : "none",
      gap: "28px",
      alignItems: "center",
    },

    navLink: {
      fontSize: "14px",
      color: "#334155",
      fontWeight: 500,
      cursor: "pointer",
      position: "relative",
      lineHeight: "52px",
      padding: "8px 0",
      display: "flex",
      alignItems: "center",
      transition: "all 0.2s ease",
      textDecoration: "none",
      "&:hover": {
        color: "#059669",
      },
    },

    activeNavLink: {
      color: "#059669",
    },

    dropdownWrapper: {
      position: "relative",
    },

    dropdown: {
      position: "absolute",
      top: "calc(100% + 5px)",
      left: 0,
      background: "#ffffff",
      padding: "12px 0",
      boxShadow: "0 20px 45px rgba(15,23,42,0.15)",
      borderRadius: "12px",
      width: "280px",
      opacity: 0,
      visibility: "hidden",
      transform: "translateY(12px)",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      zIndex: 1001,
      border: "1px solid rgba(226, 232, 240, 0.8)",
      maxHeight: "500px",
    },

    dropdownActive: {
      opacity: 1,
      visibility: "visible",
      transform: "translateY(0)",
    },

    categoryHeader: {
      padding: "10px 16px 8px",
      fontSize: "13px",
      fontWeight: 700,
      color: "#059669",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
      margin: "4px 0",
      background: "#f8fafc",
    },

    dropdownItem: {
      padding: "12px 16px",
      fontSize: "14px",
      cursor: "pointer",
      color: "#475569",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      borderLeft: "3px solid transparent",
      transition: "all 0.2s ease",
      textDecoration: "none",
      background: "none",
      border: "none",
      width: "100%",
      textAlign: "left",
      "&:hover": {
        background: "#f0fdf4",
        color: "#059669",
        borderLeft: "3px solid #059669",
        paddingLeft: "20px",
      },
    },

    loadingItem: {
      padding: "16px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      color: "#64748b",
      fontSize: "14px",
    },

    errorItem: {
      padding: "16px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      color: "#dc2626",
      fontSize: "14px",
    },

    callButton: {
      background: "#059669",
      color: "#ffffff",
      padding: "12px 24px",
      border: "none",
      cursor: "pointer",
      fontSize: "15px",
      fontWeight: 600,
      borderRadius: "8px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
      transition: "all 0.3s ease",
      textDecoration: "none",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 12px 30px rgba(5, 150, 105, 0.4)",
        background: "#047857",
      },
    },

    // Mobile Header
    mobileHeader: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      height: "70px",
      display: isDesktop ? "none" : "flex",
      alignItems: "center",
      background: "#ffffff",
      boxShadow: "0 2px 10px rgba(15,23,42,0.1)",
      padding: "0 20px",
      zIndex: 1000,
      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
      justifyContent: "space-between",
    },

    mobileMenuButton: {
      display: isDesktop ? "none" : "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
      color: "#ffffff",
      border: "none",
      borderRadius: "8px",
      padding: "12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "scale(1.05)",
      },
    },

    // Sidebar
    sidebar: {
      position: "fixed",
      top: 0,
      left: sidebarOpen ? 0 : "-300px",
      width: "300px",
      height: "100vh",
      background:
        "linear-gradient(160deg, #ffffff 0%, #f8fafc 30%, #ecfdf5 100%)",
      padding: "24px 0",
      display: isDesktop ? "none" : "flex",
      flexDirection: "column",
      gap: "8px",
      zIndex: 1001,
      transition: "left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRight: "1px solid rgba(226, 232, 240, 0.8)",
      overflowY: "auto",
    },

    sidebarHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      margin: "0 16px 24px",
      paddingBottom: "20px",
      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
    },

    sidebarLogoImage: {
      height: "42px",
      width: "auto",
      objectFit: "contain",
    },

    sidebarCloseBtn: {
      border: "none",
      background: "rgba(15,23,42,0.05)",
      borderRadius: "10px",
      padding: "10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s ease",
      "&:hover": {
        background: "rgba(15,23,42,0.1)",
      },
    },

    sidebarLink: {
      fontSize: "16px",
      color: "#334155",
      fontWeight: 600,
      padding: "14px 20px",
      borderRadius: "12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      background: "transparent",
      border: "none",
      transition: "all 0.3s ease",
      textAlign: "left",
      width: "calc(100% - 40px)",
      margin: "0 20px",
      textDecoration: "none",
      "&:hover": {
        background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
        color: "#059669",
        transform: "translateX(8px)",
      },
    },

    sidebarLinkActive: {
      background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
      color: "#059669",
    },

    sidebarSubWrapper: (open) => ({
      maxHeight: open ? "500px" : "0",
      overflow: "hidden",
      transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      marginLeft: "40px",
      borderLeft: "2px solid #e2e8f0",
    }),

    sidebarCategoryHeader: {
      fontSize: "13px",
      fontWeight: 700,
      color: "#059669",
      padding: "12px 24px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginTop: "8px",
      background: "#f8fafc",
    },

    sidebarSubLink: {
      fontSize: "14px",
      color: "#475569",
      padding: "12px 24px",
      borderRadius: "10px",
      cursor: "pointer",
      background: "transparent",
      border: "none",
      textAlign: "left",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      width: "calc(100% - 48px)",
      margin: "4px 0",
      textDecoration: "none",
      "&:hover": {
        background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
        color: "#059669",
        transform: "translateX(8px)",
      },
    },

    sidebarSubLinkActive: {
      background: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
      color: "#059669",
    },

    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(15,23,42,0.6)",
      opacity: sidebarOpen ? 1 : 0,
      visibility: sidebarOpen ? "visible" : "hidden",
      transition: "all 0.4s ease",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
    },
  };

  // Render Desktop Projects Dropdown
  const renderDesktopProjectsDropdown = () => {
    if (loading) {
      return (
        <div style={styles.loadingItem}>
          <FiAlertCircle size={16} />
          Loading projects...
        </div>
      );
    }

    if (error && Object.keys(projectsByCategory).length === 0) {
      return (
        <div style={styles.errorItem}>
          <FiAlertCircle size={16} />
          {error}
        </div>
      );
    }

    if (Object.keys(projectsByCategory).length === 0) {
      return (
        <div style={{ ...styles.dropdownItem, justifyContent: "center" }}>
          No projects available
        </div>
      );
    }

    return Object.entries(projectsByCategory).map(([category, projects]) => (
      <React.Fragment key={category}>
        <div style={styles.categoryHeader}>{category}</div>
        {projects.map((project) => (
          <Link
            key={project.path}
            to={project.path}
            style={styles.dropdownItem}
            onClick={() => setActiveDropdown(null)}
          >
            {project.icon}
            {project.text}
          </Link>
        ))}
      </React.Fragment>
    ));
  };

  // Render Mobile Projects Dropdown
  const renderMobileProjectsDropdown = () => {
    if (loading) {
      return (
        <div
          style={{ padding: "16px 24px", color: "#64748b", fontSize: "14px" }}
        >
          Loading projects...
        </div>
      );
    }

    if (error && Object.keys(projectsByCategory).length === 0) {
      return (
        <div
          style={{ padding: "16px 24px", color: "#dc2626", fontSize: "14px" }}
        >
          {error}
        </div>
      );
    }

    if (Object.keys(projectsByCategory).length === 0) {
      return (
        <div
          style={{ padding: "16px 24px", color: "#64748b", fontSize: "14px" }}
        >
          No projects available
        </div>
      );
    }

    return Object.entries(projectsByCategory).map(([category, projects]) => (
      <React.Fragment key={category}>
        <div style={styles.sidebarCategoryHeader}>{category}</div>
        {projects.map((project) => (
          <Link
            key={project.path}
            to={project.path}
            style={{
              ...styles.sidebarSubLink,
              ...(isActiveRoute(project.path) && styles.sidebarSubLinkActive),
            }}
            onClick={handleMobileLinkClick}
          >
            {project.icon}
            {project.text}
          </Link>
        ))}
      </React.Fragment>
    ));
  };

  return (
    <div style={styles.appWrapper}>
      {/* Desktop Header */}
      <header style={styles.header}>
        <div style={styles.container}>
          <Link to="/" style={styles.logo}>
            <img
              src={logo}
              alt="Mitha Constructions"
              style={styles.logoImage}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.innerHTML = `
                  <div style="display: flex; flex-direction: column;">
                    <div style="font-size: 22px; font-weight: 800; color: #059669;">MITHACON</div>
                    <div style="font-size: 9px; font-weight: 600; color: #64748b;">CONSTRUCTIONS</div>
                  </div>
                `;
              }}
            />
          </Link>

          <nav style={styles.nav}>
            {navItems.map((item) =>
              item.dropdown ? (
                <div
                  key={item.id}
                  style={styles.dropdownWrapper}
                  onMouseEnter={() => setActiveDropdown(item.id)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.path}
                    style={{
                      ...styles.navLink,
                      ...(isActiveRoute(item.path) && styles.activeNavLink),
                      ...(activeDropdown === item.id && { color: "#059669" }),
                    }}
                  >
                    {item.label}
                    <FiChevronDown
                      style={{
                        marginLeft: "6px",
                        transition: "transform 0.25s ease",
                        transform:
                          activeDropdown === item.id
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                      }}
                    />
                  </Link>
                  <div
                    style={{
                      ...styles.dropdown,
                      ...(activeDropdown === item.id && styles.dropdownActive),
                      ...(item.id === "projects" && { width: "320px" }),
                    }}
                  >
                    {item.id === "about" &&
                      aboutDropdownItems.map((subItem) => (
                        <Link
                          key={subItem.text}
                          to={subItem.path}
                          style={styles.dropdownItem}
                          onClick={() => setActiveDropdown(null)}
                        >
                          {subItem.icon}
                          {subItem.text}
                        </Link>
                      ))}
                    {item.id === "projects" && renderDesktopProjectsDropdown()}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.id}
                  to={item.path}
                  style={{
                    ...styles.navLink,
                    ...(isActiveRoute(item.path) && styles.activeNavLink),
                  }}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      </header>

      {/* Mobile Header */}
      <header style={styles.mobileHeader}>
        <Link to="/" style={styles.logo}>
          <img
            src={logo}
            alt="Mitha Constructions"
            style={styles.logoImage}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </Link>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <a
            href="tel:+1234567890"
            style={{
              ...styles.callButton,
              padding: "10px 16px",
              fontSize: "14px",
            }}
          >
            <FiPhone size={16} />
            Call
          </a>

          <button
            style={styles.mobileMenuButton}
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu size={22} />
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <Link to="/" style={styles.logo} onClick={handleMobileLinkClick}>
            <img
              src={logo}
              alt="Mitha Constructions"
              style={styles.sidebarLogoImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Link>
          <button
            style={styles.sidebarCloseBtn}
            onClick={() => setSidebarOpen(false)}
          >
            <FiX size={20} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {navItems.map((item) =>
            item.dropdown ? (
              <React.Fragment key={item.id}>
                <button
                  style={{
                    ...styles.sidebarLink,
                    ...((mobileDropdown === item.id ||
                      isActiveRoute(item.path)) &&
                      styles.sidebarLinkActive),
                  }}
                  onClick={() => toggleMobileDropdown(item.id)}
                >
                  <span>{item.label}</span>
                  <FiChevronDown
                    style={{
                      transform:
                        mobileDropdown === item.id
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                    }}
                  />
                </button>
                <div
                  style={styles.sidebarSubWrapper(mobileDropdown === item.id)}
                >
                  {item.id === "about" &&
                    aboutDropdownItems.map((subItem) => (
                      <Link
                        key={subItem.text}
                        to={subItem.path}
                        style={{
                          ...styles.sidebarSubLink,
                          ...(isActiveRoute(subItem.path) &&
                            styles.sidebarSubLinkActive),
                        }}
                        onClick={handleMobileLinkClick}
                      >
                        {subItem.icon}
                        {subItem.text}
                      </Link>
                    ))}
                  {item.id === "projects" && renderMobileProjectsDropdown()}
                </div>
              </React.Fragment>
            ) : (
              <Link
                key={item.id}
                to={item.path}
                style={{
                  ...styles.sidebarLink,
                  ...(isActiveRoute(item.path) && styles.sidebarLinkActive),
                }}
                onClick={handleMobileLinkClick}
              >
                <span>{item.label}</span>
              </Link>
            ),
          )}

          <a
            href="tel:+1234567890"
            style={{
              ...styles.callButton,
              margin: "24px 20px 0",
              justifyContent: "center",
            }}
            onClick={handleMobileLinkClick}
          >
            <FiPhone size={18} />
            Call Us Now
          </a>
        </div>
      </aside>

      {/* Overlay */}
      <div style={styles.overlay} onClick={() => setSidebarOpen(false)} />
    </div>
  );
};

export default ConstructionNavbar;
